// Shared Dromo hooks for data transformation

export const downcaseEmail = (record) => {
  if (record.row.email && record.row.email.value) {
    record.row.email.value = record.row.email.value.toLowerCase()
  }
  return record
}

export const standardizeFullName = (values) => {
  return values.map((item) => {
    if (item.value && item.value.includes(",")) {
      // "Smith, John" -> ["Smith", "John"] -> ["John", "Smith"] -> "John Smith"
      const parts = item.value
        .split(",")
        .map((part) => part.trim())
        .filter((p) => p)
      return {
        index: item.index,
        value: parts.reverse().join(" "),
      }
    }
    return {
      index: item.index,
      value: item.value,
    }
  })
}

export const addLeadingZeroesToZip = (values) => {
  return values.map((item) => {
    if (!item.value || item.value.length >= 5) {
      return {
        index: item.index,
        value: item.value,
      }
    }

    // Pad with leading zeros until length is 5
    let paddedZip = item.value
    while (paddedZip.length < 5) {
      paddedZip = "0" + paddedZip
    }

    return {
      index: item.index,
      value: paddedZip,
    }
  })
}

// Helper for creating Dromo row validators
// Usage: fieldValidator("field_name").validate("Error message", (record) => isValid)
// Return true if valid, false if invalid
const fieldValidator = (field) => ({
  validate: (message, validateFn) => (record) => {
    const isValid = validateFn(record)
    const existingInfo = record.row[field]?.info || []
    const unrelatedInfo = existingInfo.filter((i) => i.message !== message)

    return {
      ...record,
      row: {
        ...record.row,
        [field]: {
          ...record.row[field],
          info: isValid
            ? unrelatedInfo
            : [...unrelatedInfo, { message, level: "error" }],
        },
      },
    }
  },
})

export const validateStartDateBeforeEndDate = fieldValidator(
  "end_date"
).validate("Start date must be before end date", (record) => {
  const startDate = record.row.start_date?.resultValue
  const endDate = record.row.end_date?.resultValue
  if (!startDate || !endDate) return true
  return new Date(startDate) <= new Date(endDate)
})

export const validateCheckInBeforeCheckOut = fieldValidator(
  "check_out_date"
).validate("Check-in date must be before check-out date", (record) => {
  const checkInDate = record.row.check_in_date?.resultValue
  const checkOutDate = record.row.check_out_date?.resultValue
  if (!checkInDate || !checkOutDate) return true
  return new Date(checkInDate) <= new Date(checkOutDate)
})

const MONTHLY_PRICING_STRUCTURES = [
  "per_month",
  "by_foot_per_month",
  "sqft_per_month",
]

export const validatePricingStructureInGroup = (contractGroups) => {
  return fieldValidator("pricing_structure").validate(
    "Monthly billing contracts require a monthly pricing structure (Per Month, By Foot Per Month, or Sq Ft Per Month)",
    (record) => {
      const groupId = record.row.contract_group_id?.resultValue
      const pricingStructure = record.row.pricing_structure?.resultValue
      if (!groupId || !pricingStructure) return true

      const selectedGroup = contractGroups.find((g) => g.id === groupId)
      return (
        !selectedGroup?.monthlyBilling ||
        MONTHLY_PRICING_STRUCTURES.includes(pricingStructure)
      )
    }
  )
}

export const validateMonthlyBillingRequiresMonthlyPricing = fieldValidator(
  "pricing_structure"
).validate(
  "Monthly billing schedule requires a monthly pricing structure (Per Month, By Foot Per Month, or Sq Ft Per Month)",
  (record) => {
    const billingSchedule = record.row.billing_schedule?.resultValue
    const pricingStructure = record.row.pricing_structure?.resultValue

    if (billingSchedule !== "monthly") return true
    if (!pricingStructure) return false

    return MONTHLY_PRICING_STRUCTURES.includes(pricingStructure)
  }
)

export const validateLowQuantityBalanceThresholdAbsentUnlessIsInventory =
  fieldValidator("low_quantity_balance_threshold").validate(
    "Low balance point must be empty unless tracking inventory",
    (record) => {
      const isInventory = record.row.is_inventory?.resultValue
      const lowQuantityBalanceThreshold =
        record.row.low_quantity_balance_threshold?.resultValue

      return isInventory === true ? true : lowQuantityBalanceThreshold === ""
    }
  )

export const validateInitialReadingForNewMetersOnly = (meterNames) => {
  return fieldValidator("initial_reading").validate(
    "Initial reading can only be provided for new meters, not existing ones.",
    (record) => {
      const name = record.row.name?.resultValue
      const initialReading = record.row.initial_reading?.resultValue

      if (!name || !initialReading) return true

      const exists = meterNames.find(
        (existingMeterName) =>
          existingMeterName.trim().toLowerCase() === name.toLowerCase()
      )

      return !exists
    }
  )
}

export const validateOverwriteIfMeterNameExists = (meterNames) => {
  return fieldValidator("name").validate(
    "A meter with this name already exists but you have not indicated you want to overwrite data. Please check the box to overwrite or change the meter name to one not already in use.",
    (record) => {
      const importMeterName = record.row.name?.resultValue
      const overwrite = record.row.overwrite?.resultValue

      if (!importMeterName || overwrite) return true

      const existingMeter = meterNames.find(
        (existingMeterName) =>
          existingMeterName.trim().toLowerCase() ===
          importMeterName.toLowerCase()
      )

      return !existingMeter && !overwrite
    }
  )
}

export const validateOverwriteIfProductNameExists = (productNames) => {
  return fieldValidator("name").validate(
    "A standard-type item with this name already exists but you have not indicated you want to overwrite data. Please check the box to overwrite or change the item name to one not already in use.",
    (record) => {
      const importProductName = record.row.name?.resultValue
      const overwrite = record.row.overwrite?.resultValue

      if (!importProductName || overwrite) return true

      const existingProduct = productNames.find(
        (existingProductName) =>
          existingProductName.trim().toLowerCase() ===
          importProductName.toLowerCase()
      )

      return !existingProduct && !overwrite
    }
  )
}

export const validateOverwriteIfRemovingGlCode = (removeGlCodeValue) => {
  return fieldValidator("gl_code").validate(
    "Overwrite must be checked to remove an existing G/L code mapping.",
    (record) => {
      const glCode = record.row.gl_code?.resultValue
      const overwrite = record.row.overwrite?.resultValue

      if (glCode !== removeGlCodeValue) return true
      return !!overwrite
    }
  )
}

const getAdjustedQuantityOnHand = (
  adjustmentType,
  adjustmentQuantity,
  quantityBalance
) => {
  const canCalculateAdjustedQuantity =
    !!adjustmentType && adjustmentQuantity !== ""

  if (canCalculateAdjustedQuantity) {
    return adjustmentType === "inventory_received"
      ? quantityBalance + adjustmentQuantity
      : quantityBalance - adjustmentQuantity
  } else {
    return ""
  }
}

export const manageReadOnlyQuantityColumns = (productQuantityBalances) => {
  return (record) => {
    let updatedRecord
    const productId = record.row.product_id.resultValue
    const productWithQuantityBalance = productQuantityBalances.find(
      (product) => product.productId === productId
    )

    // if row does not have a product_id value presently, or that product_id doesn't exist in our
    // quantity balance data then we clear the value of the read-only quantity columns to avoid
    // showing stale data
    if (!productId || !productWithQuantityBalance) {
      updatedRecord = {
        ...record,
        row: {
          ...record.row,
          current_quantity_on_hand: {
            ...record.row.current_quantity_on_hand,
            value: "",
            resultValue: "",
          },
          adjusted_quantity_on_hand: {
            ...record.row.current_quantity_on_hand,
            value: "",
            resultValue: "",
          },
        },
      }
    } else {
      // the row *does* have a product_id and that product_id exists in our quantity balance data
      // so we set current_quantity_on_hand
      const quantityBalance = Number(productWithQuantityBalance.quantityBalance)

      updatedRecord = {
        ...record,
        row: {
          ...record.row,
          current_quantity_on_hand: {
            ...record.row.current_quantity_on_hand,
            value: quantityBalance,
            resultValue: quantityBalance,
          },
        },
      }

      const adjustmentType = record.row.adjustment_type.resultValue
      const adjustmentQuantity = record.row.quantity.resultValue
      const adjustedQuantityOnHand = getAdjustedQuantityOnHand(
        adjustmentType,
        adjustmentQuantity,
        quantityBalance
      )

      updatedRecord = {
        ...record,
        row: {
          ...updatedRecord.row,
          adjusted_quantity_on_hand: {
            ...record.row.adjusted_quantity_on_hand,
            value: adjustedQuantityOnHand,
            resultValue: adjustedQuantityOnHand,
          },
        },
      }
    }

    return updatedRecord
  }
}

export const addReadOnlyQuantityColumns = (instance, data) => {
  instance.addField(
    {
      label: "Current Quantity On Hand",
      key: "current_quantity_on_hand",
      type: "number",
      readOnly: true,
      description:
        "The current quantity of this item in Dockwa prior to this adjustment.",
    },
    { after: "quantity" }
  )

  instance.addField(
    {
      label: "Adjusted Quantity On Hand",
      key: "adjusted_quantity_on_hand",
      type: "number",
      readOnly: true,
      description:
        "The anticipated quantity of this item in Dockwa after this adjustment.",
    },
    { after: "current_quantity_on_hand" }
  )
}

const SQFT_PRICING_STRUCTURES = [
  "sqft_per_month",
  "sqft_per_season",
  "sqft_per_stay",
  "sqft_per_day",
]

const DIMENSIONAL_PRICING_STRUCTURES = [
  ...SQFT_PRICING_STRUCTURES,
  "by_foot_per_day",
  "by_foot_per_month",
  "by_foot_per_season",
  "by_foot_per_stay",
]

const isUsingDimensionalPricing = (
  field,
  pricingStructure,
  dimensionalPricingStructures,
  group
) => {
  const dimensionalPricingStructureInImportData =
    !!pricingStructure &&
    dimensionalPricingStructures.includes(pricingStructure)
  const groupUsesDimensionalPricing =
    group &&
    (field === "beam" ? group.twoDimensionalPricing : group.dimensionalPricing)
  const usingDimensionalPricing = pricingStructure
    ? dimensionalPricingStructureInImportData
    : groupUsesDimensionalPricing

  return usingDimensionalPricing
}

export const validateNonZeroDimensionForDimensionalPricing = (
  contractGroups,
  field
) => {
  let errorMessage =
    "Must be non-zero if using a dimensional pricing structure."
  if (contractGroups.length > 0) {
    errorMessage +=
      " If you are seeing this error when the Pricing Structure column is empty it means the selected Contract Group is using dimensional pricing."
  }
  return fieldValidator(field).validate(errorMessage, (record) => {
    const groupId = record.row.contract_group_id?.resultValue
    const fieldValue = record.row[field]?.resultValue
    const pricingStructure = record.row.pricing_structure?.resultValue

    const selectedGroup = contractGroups.find((g) => g.id === groupId)
    const relatedPricingStructures =
      field === "beam"
        ? SQFT_PRICING_STRUCTURES
        : DIMENSIONAL_PRICING_STRUCTURES

    const usingDimensionalPricing = isUsingDimensionalPricing(
      field,
      pricingStructure,
      relatedPricingStructures,
      selectedGroup
    )

    return usingDimensionalPricing ? !!fieldValue : true
  })
}

export const validateEndDateAbsentForMonthToMonth = (contractGroups) => {
  return fieldValidator("end_date").validate(
    "End date not supported for month-to-month contracts.",
    (record) => {
      const groupId = record.row.contract_group_id?.resultValue
      const endDate = record.row.end_date?.resultValue
      if (!groupId || !endDate) return true

      const selectedGroup = contractGroups.find((g) => g.id === groupId)

      return !selectedGroup?.monthToMonth
    }
  )
}
